import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { formatShortDateFromNow } from '../../../../../utils/date-utils/dateUtils';
import { buildUrlWithParameters } from '../../../../../utils/url-utils/urlUtils';
import { NotificationReadStatus } from '../status/NotificationReadStatus';
import { NotificationWatcherType } from '../../../notification.types';
import {
  sendNotificationClickedEvent,
  getNotificationTrackingParameters,
} from '../../../notificationUtils';
import { StateType } from '../../../../../state/reducers';
import { NotificationWatcherQuery } from '../../query/NotificationWatcherQuery';
import { NotificationWatcherCount } from '../../count/NotificationWatcherCount';
import { NotificationLink } from '../../link/NotificationLink';
import { NotificationLinkDate } from '../../link/NotificationLinkDate';
import { NotificationLinkContent } from '../../link/NotificationLinkContent';

type Props = {
  notification: NotificationWatcherType;
  notificationClicked: Function;
};

export const NotificationWatcher: FC<Props> = ({
  notification,
  notificationClicked,
}) => {
  const { isAlreadyRead } = useSelector(({ notifications }: StateType) => ({
    isAlreadyRead: !!notifications.notifReadStatusMap[notification.id],
  }));

  const onNotificationClicked = (
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    sendNotificationClickedEvent();
    notificationClicked(notification)(event);
  };

  const { watcher } = notification.data;
  return (
    <NotificationLink
      href={buildUrlWithParameters(
        notification.link,
        getNotificationTrackingParameters(notification),
      )}
      onClick={onNotificationClicked}
      data-test-id="notification-watcher"
    >
      <NotificationReadStatus notificationId={notification.id} />
      <NotificationLinkContent>
        <NotificationWatcherQuery isAlreadyRead={isAlreadyRead}>
          {watcher.query}
        </NotificationWatcherQuery>
        <NotificationWatcherCount>
          {watcher.numberOfNewDecisions > 0 && (
            <FormattedMessage
              id="navigation/notification/components/list/watcher/NotificationWatcher.tsx/1"
              values={{
                decisionsCount: watcher.numberOfNewDecisions,
              }}
            />
          )}
          {watcher.numberOfNewDecisions > 0 &&
            watcher.numberOfNewCommentaries > 0 && (
              <FormattedMessage id="navigation/notification/components/list/watcher/NotificationWatcher.tsx/3" />
            )}
          {watcher.numberOfNewCommentaries > 0 && (
            <FormattedMessage
              id="navigation/notification/components/list/watcher/NotificationWatcher.tsx/2"
              values={{
                commentariesCount: watcher.numberOfNewCommentaries,
              }}
            />
          )}
        </NotificationWatcherCount>
        <NotificationLinkDate>
          {formatShortDateFromNow(notification.date)}
        </NotificationLinkDate>
      </NotificationLinkContent>
    </NotificationLink>
  );
};
